import {Injectable} from '@angular/core';
import {edsEnvConfiguration} from '../../../environments/edsEnvConfiguration';
import {ApplicationCatalogActions} from '../Actions/application-catalog-actions';
import {ActionsObservable, ofType, StateObservable} from 'redux-observable-es6-compat';
import {Feature, FeatureEx} from "../../models/features/features";
import {ApplicationSpecActions} from '../Actions/application-spec-actions';
import {isNullOrUndefined} from "util";
import {Utils} from "../../common/utils";
import {ISelectorState} from "../selector.state";
import {Ic, ILedInfo, Languages} from "../../models/common";
import {
  compareFeatureFunctionOthers, compareFeatureFunctionTop,
  SIZE_TOP_FEATURES, FeatureFilterState
} from "../../models/features/feature_const";
import {XmlUtils} from "../../common/xmlUtils";
import {IInput, IOutput, ISingleOutput} from "../../models/ISpec";
import {ApplicationContextActions} from "../Actions/application-cotext-actions";
import {ApplicationToolbarActions} from "../Actions/application-toolbar-actions";
import {HttpClient} from "@angular/common/http";
import {catchError, concatMap, map, mergeMap, tap} from "rxjs/operators";
import {combineLatest, EMPTY, from, Observable, of} from "rxjs";
import {environment} from "../../../environments/environment";

const catalogMap = environment.catalogs;
const servicesMap = environment.services;

@Injectable()
export class LoadCatalogEpics {

  constructor(private http: HttpClient) { }

  static getNumberValue(o: any, field: string, def: number= NaN): number {
    return !isNullOrUndefined(o[field]) ? Number.parseFloat(o[field]) : def;
  }
  static getStringValue(o: any, field: string, def: string= null): string {
    return !isNullOrUndefined(o[field]) ? o[field] : def;
  }

  private _makeHttpRequest(catalog: string): Observable<Ic[]> {
    return this.http.get(catalog, {responseType: 'json'}).pipe(
      map((x: Ic[]) => x ),
      catchError( _ =>  of([]) )
    );
  }

  private _loadCatalog(catalogName: string): Observable<Ic[]> {
    if (!catalogName || catalogName === '' || catalogName === 'ic_digital_power') {
      return of([]); // EMPTY catalog
    } else {
      const catalogUrl = catalogMap[catalogName] ?  catalogMap[catalogName] : '/api/catalog/' + catalogName ;
      return this._makeHttpRequest(catalogUrl);
    }
  }

  // LOAD only one extra
  // catalog solutions.
  private _loadOneExtraCatalog(catalogName: string): Observable<Ic[]> {
    if (catalogName && (catalogName === 'acdc' || catalogName === 'ic_acdc_catalogs')) {
      return this._loadCatalog('pfc');
    } else if (catalogName && (catalogName === 'dcdc' || catalogName === 'ic_dcdc_catalogs')) {
      return this._loadCatalog('llc');
    } else {
      return of([]); // EMPTY catalog
    }
  }

  private _loadExtraCatalog(catalogName: string) {
    if (catalogName !== 'ic_digital_power') {
      return this._loadOneExtraCatalog(catalogName);
    } else {
      return combineLatest([
        this._loadCatalog('cllc'), // CLLC
        this._loadCatalog('pfcitpccm'), // Bi-dir CCM Totem-Pole PFC
        this._loadCatalog('llc'), // FB LLC
        this._loadCatalog('pfcitp'), // ZVS Totem-Pole PFC
        this._loadCatalog('dab'), // Dual Active Bridge Converter
        this._loadCatalog('viennabidir'), // 3-Level T-type Converter
        this._loadCatalog('vienna') // Vienna Rectifier
      ]).pipe(
        map(([pfcitpccm, dab, viennabidir, pfcitp, vienna, llc, cllc]) => pfcitpccm.concat(dab).concat(viennabidir).concat(pfcitp).concat(vienna).concat(llc).concat(cllc))
      );
    }
  }

  // LOAD descriptions in different languages
  private _loadDescriptions(designSelect) {
    const apiSrvOrigin = window.origin === 'https://eds.st.com' ? 'https://www.st.com' : 'https://qa-www2.st.com';
    const icTranslation = (lang) => this.http.get(`${apiSrvOrigin}${servicesMap['ic_translation_descriptions_srv']}?RPN=${designSelect.payload.RPN}&lang=${lang}`, {responseType: 'json'});

    return combineLatest([icTranslation(Languages.en), icTranslation(Languages.ja), icTranslation(Languages.zh)]).pipe(
      map(([x, y, z]) => ({ en: x, ja: y, zh: z })),
    );
  }

  loadCatalog = (action$) => {
    return action$.pipe(
      ofType(ApplicationCatalogActions.ActionType.LOAD),
      mergeMap(({payload}) => {
        // normalize the filter topologies array
        const _tpgFilter = payload.topologiesFilter.map(x => Utils.capitalize(x).toLowerCase());
        return combineLatest([this._loadCatalog(payload.name), this._loadExtraCatalog(payload.name)]).pipe(
          map( ([x, y]) => x.concat(y)),
        // .map(fullCatalog=>fullCatalog.filter(x=> _tpgFilter.length === 0 || _tpgFilter.includes(x.ic.topology.toLowerCase())))
          map(fullCatalog => fullCatalog.filter(
            ic => {
              return _tpgFilter.length === 0 || _tpgFilter.some(y => ic.collection != null ? ic.collection.map(z => z.toLowerCase()).includes(y) :  _tpgFilter.includes(ic.topology.toLowerCase()));
            })
            ),
          map(catalog => (ApplicationCatalogActions.loadedCatalog(catalog))),
          catchError((error: Response) => of( ApplicationCatalogActions.loadCatalogError(error.toString())))
        );
      })
    );
  }

  loadLedDBCatalog = (action$) => {
    // console.log("loadLedDBCatalog");
    return action$.pipe(
      ofType(ApplicationCatalogActions.ActionType.LOAD),
      mergeMap(({payload}) => {
        // console.log("loadLedDBCatalog", payload );
        if (payload.appClass.toLowerCase().startsWith('led')) {
          return XmlUtils.loadXmlFile(this.http, edsEnvConfiguration.edsLedDb).pipe(
            map(({Led}) => Led),
            map(({item}) => item),
            map((leds: ILedInfo[] ) => (ApplicationCatalogActions.setLedDB(leds))),
            catchError((error: Response) => { console.log('Load led Error', error); return of(ApplicationCatalogActions.setLedDB(null)); })
          );
        } else {
          return  of(ApplicationCatalogActions.setLedDB(null));
        }
      })
    );
  }

  featureCounter (arr: Feature[], label: string, category: string): number {
    // return arr.reduce((acc, curr)=> (curr.key === key)? acc+curr.count: acc, 0)
    // return arr.filter((x)=> x.feature.label.toLowerCase() === label.toLowerCase() && x.feature.category.toLowerCase() === category.toLowerCase()).length;
    const _key = Utils.keyCompose(label, category);

    return arr.filter((x) => _key.localeCompare(Utils.keyCompose(x.feature.label, x.feature.category)) === 0).length;
  }

  featureLevel (arr: Feature[], label: string, category: string): number {
    return arr.reduce((acc, curr) =>
        (curr.feature.label === label && curr.feature.category === category) ?
          (isNullOrUndefined(curr.feature.level) ? acc :
            (curr.feature.level >= acc ? acc : curr.feature.level)) : acc,
      Number.POSITIVE_INFINITY.valueOf());
  }

  loadFeatures = (action$, store) => {
    return action$.pipe(
      ofType(ApplicationCatalogActions.ActionType.LOADED),
      mergeMap(({payload}) => {

        const stdFeatures: Feature[] = store.value.appCatalog.service.stdFeatures;

        let deviceFeatures = new Array<Feature>();
        const icCount = payload.length;

        payload.forEach((item) => {deviceFeatures = deviceFeatures.concat(item.features); });

        const featureFull = stdFeatures.concat(deviceFeatures);
        // console.log("Concat",featureFull);

        const fx: FeatureEx[] = featureFull
          .map(x => {
            const countFeature = this.featureCounter(deviceFeatures, x.feature.label, x.feature.category); // only for feature in db
            const levelFeature = this.featureLevel(featureFull, x.feature.label, x.feature.category); // level for the fullFeature (standard + device)
            return {stateFilter: countFeature === icCount ? FeatureFilterState.YES : FeatureFilterState.NO_FILTER,
              key: Utils.keyCompose(x.feature.label, x.feature.category),
              longLabel: Utils.capitalize(Utils.stringCompose(x.feature.category, x.feature.label)) ,
              count: countFeature,
              isCommon: countFeature === icCount ? true : false,
              feature: {label: x.feature.label, category: x.feature.category, level: levelFeature}}; })

          .sort((a, b) => a.key.localeCompare(b.key))
          .filter((f, i, a) => i > 0 ? a[i - 1].key !== f.key && f.count > 0 : true)
          .sort(compareFeatureFunctionTop);
        // console.log("update to EX", fx );
        const topFeatures = fx.slice(0, SIZE_TOP_FEATURES);
        const otherFeatures = fx.slice(SIZE_TOP_FEATURES).sort(compareFeatureFunctionOthers);

//        console.log("Top Features", topFeatures);
//        console.log("Other Features", otherFeatures);

        // reset spec and set new features
        return from([ ApplicationSpecActions.setFeaturesFilter(topFeatures.concat(otherFeatures)), ApplicationCatalogActions.loadComplete(), ApplicationSpecActions.resetSpec(), ApplicationContextActions.setSpecsFromContext()]);
      })
    );
  }

  selectDesignByKey =  (action$, store) => {
    const searchSolution: ActionsObservable<any> =  action$.pipe(ofType(ApplicationCatalogActions.ActionType.SELECT_SOLUTION_BY_KEY));
    const loaded: ActionsObservable<any> =  action$.pipe(ofType(ApplicationCatalogActions.ActionType.LOADED));

    return combineLatest([searchSolution, loaded]).pipe(
      mergeMap(([ss, ld]) => {
        const updCreatorFn = store.value.appCatalog.service.updateDesignCreator;
        const appSpec = store.value.appSpec;

        const solutionToFind: string = ss.payload;
        const db: Ic[] = ld.payload;
        const _icSelected: Ic =  db.find((ic) => ic.key === solutionToFind);

        // validate ic selected
        const designSelect: Ic = isNullOrUndefined(_icSelected)
          ? null
          : updCreatorFn(_icSelected).updateDesign(_icSelected, appSpec);

        if (_icSelected) {
          return of(ApplicationCatalogActions.selectSolution(designSelect), ApplicationCatalogActions.loadDescriptionsOfSelectedSolution(designSelect));
        }

        return of(ApplicationCatalogActions.selectSolution(designSelect));
      })
    );

  }

  loadDescriptionsOfSelectedSolution = (action$) => {
    return action$.pipe(
      ofType(ApplicationCatalogActions.ActionType.LOAD_DESCRIPTIONS_OF_SELECTED_SOLUTION),
      mergeMap((designSelect) => {
        return this._loadDescriptions(designSelect).pipe(
          map(({en, zh, ja}) => (ApplicationCatalogActions.loadedDescriptionsOfSelectedSolution({en, zh, ja}))),
          catchError((error: Response) => of(ApplicationCatalogActions.loadedDescriptionsOfSelectedSolutionError(error ? error.toString() : "Load descriptions ic error unknown")))
        );
      })
    );
  }

  logAllEvents = (action$: ActionsObservable<any>, store: StateObservable<ISelectorState>) => {
    return action$.pipe(
      ofType(ApplicationSpecActions.ActionType.RESET_SPECS),
      // .do((x) => console.log('EVENT', x,  new Date().toISOString()))
      mergeMap(x => EMPTY)
    );
  }

  importSpecificationFromWidget =  (action$, store: StateObservable<ISelectorState>) => {
    return  action$.pipe(
      ofType(ApplicationContextActions.ActionType.SET_SPECS_FROM_CONTEXT),
      // .do((x) => console.log('SET SPEC FROM CONTEXT', x, new Date().toISOString()))
      mergeMap((_) => {
        const _fromWidget = store.value.appContext.context;
        // console.log('appContexWidget 1',_fromWidget);
        // for (let key in _fromWidget){console.log('key', key)}
        if (_fromWidget !== null) {
          const _input: IInput<number> = {
            min: LoadCatalogEpics.getNumberValue(_fromWidget, 'msg.data.specs.input.v.min'),
            max: LoadCatalogEpics.getNumberValue(_fromWidget, 'msg.data.specs.input.v.max'),
            freq: LoadCatalogEpics.getNumberValue(_fromWidget, 'msg.data.specs.input.f')
          };

          const _outputs: ISingleOutput<number>[] = [];

          const _singleOutLed: ISingleOutput<number> = {
            voltage: LoadCatalogEpics.getNumberValue(_fromWidget, 'msg.data.specs.led.VF'),
            current: LoadCatalogEpics.getNumberValue(_fromWidget, 'msg.data.specs.led.IF'),
          };
          if (!isNaN(_singleOutLed.voltage) || !isNaN(_singleOutLed.current)) {
            _outputs.push(_singleOutLed);
          } else {
            for (let idx = 0; idx < 10; idx++) {
              const _singleOut: ISingleOutput<number> = {
                voltage: LoadCatalogEpics.getNumberValue(_fromWidget, 'msg.data.specs.output.' + idx + '.v'),
                current: LoadCatalogEpics.getNumberValue(_fromWidget, 'msg.data.specs.output.' + idx + '.i'),
              };
              if (!isNaN(_singleOut.voltage) || !isNaN(_singleOut.current) || idx === 0) {
                _outputs.push(_singleOut);
              }
            }
          }
          const _output: IOutput<number> = {
            outputs: _outputs,
            globalOutput: {voltage: NaN, current: NaN},
            ledParam: {
              serial: LoadCatalogEpics.getNumberValue(_fromWidget, 'msg.data.specs.numLed.serie'),
              parallel: LoadCatalogEpics.getNumberValue(_fromWidget, 'msg.data.specs.numLed.parallel'),
              rd: LoadCatalogEpics.getNumberValue(_fromWidget, 'msg.data.specs.led.Rd'),
              selectedLed: null
            }
          };

          const _searchCpn =
            LoadCatalogEpics.getStringValue(_fromWidget, 'msg.data.specs.rpn') ||
            LoadCatalogEpics.getStringValue(_fromWidget, 'msg.data.specs.RPN') ||
            LoadCatalogEpics.getStringValue(_fromWidget, 'msg.data.specs.Rpn') ||
            '';

          // console.log("importSpecificationFromWidget", _input, _output);
          return from([
            ApplicationSpecActions.setInputLimits(_input),   // set Input
            ApplicationSpecActions.setOutputValue(_output),  // set Output
            ApplicationToolbarActions.setSearch(_searchCpn), // filter db with the CPN
            ApplicationContextActions.setContext(null)       // Reset setting from widget
          ]);

        } else {
          return EMPTY;
        }
      })
    );
  }
}
